/**
 * Hooks para Bienes Raíces (DJ 1835).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

export type DestinationCode =
  | 'habitacional' | 'comercial' | 'industrial' | 'oficinas'
  | 'bodegas' | 'estacionamiento' | 'agricola' | 'otro'

export type PropertyTypeCode = 'dfl2' | 'agricola' | 'no_agricola'

export interface RealEstate {
  id: string
  rol_avaluo: string
  alias: string
  address: string
  comuna: string
  region: string
  destination: DestinationCode
  destination_display: string
  property_type: PropertyTypeCode
  property_type_display: string
  avaluo_fiscal: string
  is_active: boolean
  notes: string
  created_at: string
  updated_at: string
}

export interface RealEstatePayload {
  rol_avaluo: string
  alias?: string
  address: string
  comuna: string
  region?: string
  destination: DestinationCode
  property_type: PropertyTypeCode
  avaluo_fiscal?: string
  is_active?: boolean
  notes?: string
}

interface PaginatedResponse<T> {
  results: T[]
  count: number
}

export function useRealEstates(params: { is_active?: boolean } = {}) {
  return useQuery<PaginatedResponse<RealEstate>>({
    queryKey: ['real-estate', params],
    queryFn: async () => {
      const search: Record<string, string> = {}
      if (params.is_active !== undefined) search.is_active = String(params.is_active)
      const { data } = await apiClient.get('/real-estate/', { params: search })
      return data
    },
  })
}

export function useCreateRealEstate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: RealEstatePayload) =>
      (await apiClient.post<RealEstate>('/real-estate/', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['real-estate'] }),
  })
}

export function useUpdateRealEstate(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (payload: Partial<RealEstatePayload>) =>
      (await apiClient.patch<RealEstate>(`/real-estate/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['real-estate'] }),
  })
}

export function useDeleteRealEstate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/real-estate/${id}/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['real-estate'] }),
  })
}

export interface DJ1835Item {
  rol_avaluo: string
  address: string
  comuna: string
  destination: string
  property_type: string
  tenant_tax_id: string
  tenant_name: string
  rent_total: string
  document_count: number
}

export interface DJ1835Preview {
  year: number
  items: DJ1835Item[]
  totals: { rent_total: string; count: number }
}

export function useDJ1835Preview(year: number, enabled = false) {
  return useQuery<DJ1835Preview>({
    queryKey: ['dj-1835', year],
    queryFn: async () =>
      (await apiClient.get(`/real-estate/dj-1835/${year}/preview/`)).data,
    enabled,
  })
}
